import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Plus, Trash2, Ban, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { AccountSelect, accountLabel, DateField, Empty, money, PageTitle, SOURCE_LABEL, today, useAccounts, yearStart } from './shared';

const blankLine = () => ({ accountId: '', debit: '', credit: '', memo: '' });

function NewEntry({ open, onClose, accounts, onSaved }) {
  const [date, setDate] = useState(today());
  const [memo, setMemo] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const [busy, setBusy] = useState(false);
  const dr = lines.reduce((a, l) => a + (Number(l.debit) || 0), 0);
  const cr = lines.reduce((a, l) => a + (Number(l.credit) || 0), 0);
  const diff = Math.round((dr - cr) * 100) / 100;
  const set = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    setBusy(true);
    try {
      await ledgerApi.createJournal({ date, memo, reference, lines });
      toast.success('Journal entry posted');
      setLines([blankLine(), blankLine()]); setMemo(''); setReference('');
      onSaved(); onClose();
    } catch (e) {
      toast.error(apiError(e, 'Could not post the entry'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>New journal entry</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">For opening balances, owner contributions, loans, corrections and anything without an invoice, payment, expense or bill of its own.</p>
        <div className="grid grid-cols-3 gap-3">
          <DateField label="Date" value={date} onChange={setDate} />
          <div><Label className="text-xs text-slate-500">Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1 h-9" placeholder="Optional" /></div>
          <div><Label className="text-xs text-slate-500">Memo</Label><Input value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1 h-9" placeholder="What this entry is for" /></div>
        </div>
        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-[1fr_110px_110px_1fr_32px] gap-2 text-[11px] uppercase text-slate-400 font-semibold"><span>Account</span><span className="text-right">Debit</span><span className="text-right">Credit</span><span>Line memo</span><span /></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_1fr_32px] gap-2">
              <AccountSelect accounts={accounts} value={l.accountId} onChange={(v) => set(i, { accountId: v })} className="h-9" testId={`je-acct-${i}`} />
              <Input type="number" step="0.01" min="0" value={l.debit} onChange={(e) => set(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} className="h-9 text-right" data-testid={`je-debit-${i}`} />
              <Input type="number" step="0.01" min="0" value={l.credit} onChange={(e) => set(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} className="h-9 text-right" data-testid={`je-credit-${i}`} />
              <Input value={l.memo} onChange={(e) => set(i, { memo: e.target.value })} className="h-9" />
              <Button variant="ghost" size="icon" className="h-9 w-8" disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
            </div>
          ))}
          <div className="flex justify-between items-center pt-1">
            <Button variant="outline" size="sm" className="gap-1" onClick={() => setLines((ls) => [...ls, blankLine()])}><Plus className="w-3.5 h-3.5" />Add line</Button>
            <div className="text-sm tabular-nums">
              <span className="text-slate-500 mr-3">Debits {money(dr)} · Credits {money(cr)}</span>
              <Badge className={`border-0 ${diff === 0 && dr > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>{diff === 0 ? (dr > 0 ? 'Balanced' : 'Enter amounts') : `Off by ${money(Math.abs(diff))}`}</Badge>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy || diff !== 0 || dr <= 0} onClick={save} data-testid="je-save">{busy ? 'Posting…' : 'Post entry'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Journal() {
  const [accounts] = useAccounts();
  const byId = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [source, setSource] = useState('all');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    ledgerApi.entries({ date_from: from, date_to: to, source: source === 'all' ? '' : source, limit: 1500 })
      .then(setData).catch((e) => toast.error(apiError(e, 'Could not load the journal')));
  }, [from, to, source]);
  useEffect(() => { load(); }, [load]);

  const onVoid = async (e) => {
    if (!window.confirm(`Void ${e.number || 'this entry'}? It stays on file, marked void, and stops counting.`)) return;
    try { await ledgerApi.voidJournal(e.sourceId); toast.success('Entry voided'); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-journal">
      <PageTitle title="Journal" subtitle="Every posting in the books. Entries from invoices, payments, expenses and bills are created automatically.">
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        <div>
          <Label className="text-xs text-slate-500">Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="mt-1 h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All sources</SelectItem>{Object.entries(SOURCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5 h-9" data-testid="new-journal-btn"><Plus className="w-4 h-4" />New entry</Button>
      </PageTitle>
      {data?.skipped?.length ? (
        <Card className="p-3 border-amber-200 bg-amber-50 text-sm text-amber-900">
          {data.skipped.length} record{data.skipped.length === 1 ? '' : 's'} could not be posted (for example: {data.skipped[0].kind} {data.skipped[0].id} — {data.skipped[0].reason}). See Close Books for the full list.
        </Card>
      ) : null}
      <Card className="border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 text-sm text-slate-500">{data ? `${data.total} entries${data.total > data.entries.length ? ` · showing the latest ${data.entries.length}` : ''}` : 'Loading…'}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="w-8" /><th className="px-3 py-3 text-left">Date</th><th className="px-3 py-3 text-left">No.</th><th className="px-3 py-3 text-left">Source</th><th className="px-3 py-3 text-left">Memo</th><th className="px-3 py-3 text-right">Amount</th><th className="px-3 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.entries || []).map((e) => {
                const amt = e.lines.reduce((a, l) => a + l.debit, 0);
                const isOpen = expanded[e.id];
                return (
                  <React.Fragment key={e.id}>
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))}>
                      <td className="pl-3 text-slate-400">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{e.date}</td>
                      <td className="px-3 py-2.5 text-slate-600">{e.number}</td>
                      <td className="px-3 py-2.5"><Badge className={`border-0 ${e.automatic ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-700'}`}>{SOURCE_LABEL[e.source] || e.source}</Badge></td>
                      <td className="px-3 py-2.5 text-slate-800">{e.memo}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(amt)}</td>
                      <td className="px-3 py-2.5 text-right">{!e.automatic && ['journal', 'bank'].includes(e.source) ? (
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-slate-500" onClick={(ev) => { ev.stopPropagation(); onVoid(e); }}><Ban className="w-3.5 h-3.5" />Void</Button>
                      ) : null}</td>
                    </tr>
                    {isOpen && e.lines.map((l, i) => (
                      <tr key={`${e.id}-${i}`} className="bg-slate-50/60 text-xs">
                        <td /><td /><td colSpan={2} className="px-3 py-1.5 text-slate-700">{accountLabel(byId[l.accountId]) || l.accountId}</td>
                        <td className="px-3 py-1.5 text-slate-500">{l.memo}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{l.debit ? `Dr ${money(l.debit)}` : `Cr ${money(l.credit)}`}</td><td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {data && !data.entries.length ? <Empty>No entries in this period.</Empty> : null}
        </div>
      </Card>
      <NewEntry open={open} onClose={() => setOpen(false)} accounts={accounts} onSaved={load} />
    </div>
  );
}
